import React, { useState, Children } from 'react';
import { motion } from 'framer-motion';
import { ArrowRightIcon, ShieldCheckIcon } from 'lucide-react';
interface HeroProps {
  onSearch?: (service: string, zip: string) => void;
}
export function Hero({
  onSearch
}: HeroProps) {
  const [service, setService] = useState('');
  const [zip, setZip] = useState('');
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (onSearch && service && zip) {
      onSearch(service, zip);
    } else if (!service) {
      alert('Please select a service type');
    } else if (!zip) {
      alert('Please enter your zip code');
    }
  };
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: 'easeOut'
      }
    }
  };
  return <section className="relative pt-32 pb-20 md:pt-40 md:pb-28 bg-gradient-to-b from-slate-900 to-slate-800 overflow-hidden" data-id="element-970">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-971">
        <motion.div className="text-center max-w-4xl mx-auto" variants={containerVariants} initial="hidden" animate="visible" data-id="element-972">
          <motion.h1 variants={itemVariants} className="text-4xl md:text-6xl font-extrabold text-white tracking-tight mb-6" data-id="element-973">
            Find Trusted Local Pros.
            <br className="hidden md:block" data-id="element-974" /> Compare Free Quotes.
          </motion.h1>

          <motion.p variants={itemVariants} className="text-lg md:text-xl text-slate-300 mb-10 max-w-2xl mx-auto" data-id="element-975">
            Connect with vetted service professionals in your area. Get multiple
            quotes, compare prices, and hire with confidence.
          </motion.p>

          <motion.div variants={itemVariants} className="max-w-3xl mx-auto bg-white rounded-2xl p-2 md:p-3 shadow-xl" data-id="element-976">
            <form className="flex flex-col md:flex-row gap-3" onSubmit={handleSubmit} data-id="element-977">
              <div className="flex-1 relative" data-id="element-978">
                <label htmlFor="service-type" className="sr-only" data-id="element-979">
                  Select Service
                </label>
                <select id="service-type" value={service} onChange={e => setService(e.target.value)} className="w-full h-14 md:h-16 px-4 bg-slate-50 border border-slate-200 rounded-xl text-slate-700 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent appearance-none" data-id="element-980">
                  <option value="" disabled data-id="element-981">
                    What do you need help with?
                  </option>
                  <optgroup label="Bathroom" data-id="element-982">
                    <option value="walk-in-tubs" data-id="element-983">Walk-In Tubs</option>
                    <option value="bathroom-remodel" data-id="element-984">Bathroom Remodel</option>
                  </optgroup>
                  <optgroup label="Kitchen" data-id="element-985">
                    <option value="kitchen-remodel" data-id="element-986">Kitchen Remodel</option>
                  </optgroup>
                  <optgroup label="Exterior" data-id="element-987">
                    <option value="roofing" data-id="element-988">Roofing</option>
                    <option value="siding" data-id="element-989">Siding</option>
                    <option value="window-replacement" data-id="element-990">
                      Window Replacement
                    </option>
                    <option value="doors" data-id="element-991">Doors</option>
                    <option value="gutter-guards" data-id="element-992">Gutter Guards</option>
                  </optgroup>
                  <optgroup label="Systems" data-id="element-993">
                    <option value="hvac" data-id="element-994">HVAC</option>
                    <option value="plumbing" data-id="element-995">Plumbing</option>
                    <option value="electrical" data-id="element-996">Electrical</option>
                    <option value="solar" data-id="element-997">Solar Panels</option>
                    <option value="home-security" data-id="element-998">Home Security</option>
                  </optgroup>
                  <optgroup label="Interior" data-id="element-999">
                    <option value="flooring" data-id="element-1000">Flooring</option>
                    <option value="stairlifts" data-id="element-1001">Stairlifts</option>
                  </optgroup>
                  <optgroup label="Outdoor & Other" data-id="element-1002">
                    <option value="pest-control" data-id="element-1003">Pest Control</option>
                  </optgroup>
                </select>
              </div>

              <div className="flex-1 relative" data-id="element-1004">
                <label htmlFor="zip-code" className="sr-only" data-id="element-1005">
                  Zip Code
                </label>
                <input type="text" id="zip-code" value={zip} onChange={e => setZip(e.target.value.replace(/\D/g, '').slice(0, 5))} placeholder="Enter your zip code" maxLength={5} className="w-full h-14 md:h-16 px-4 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent" data-id="element-1006" />
              </div>

              <button type="submit" className="h-14 md:h-16 px-8 bg-amber-500 hover:bg-amber-600 text-white font-semibold rounded-xl transition-colors flex items-center justify-center gap-2 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2 focus:ring-offset-white" data-id="element-1007">
                Get Free Quotes
                <ArrowRightIcon className="w-5 h-5" data-id="element-1008" />
              </button>
            </form>
          </motion.div>

          <motion.div variants={itemVariants} className="mt-8 flex items-center justify-center gap-2 text-slate-300 text-sm font-medium" data-id="element-1009">
            <ShieldCheckIcon className="w-5 h-5 text-amber-500" data-id="element-1010" />
            <span data-id="element-1011">Trusted by 50,000+ homeowners across the country</span>
          </motion.div>
        </motion.div>
      </div>

      {/* Background decorative elements */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none" data-id="element-1012">
        <div className="absolute -top-[20%] -right-[10%] w-[50%] h-[50%] rounded-full bg-slate-700/20 blur-3xl" data-id="element-1013" />
        <div className="absolute top-[60%] -left-[10%] w-[40%] h-[40%] rounded-full bg-amber-500/10 blur-3xl" data-id="element-1014" />
      </div>
    </section>;
}